import axios, { AxiosError } from "axios";
import { config } from "../config";
import type { RefreshedSession } from "./httpGateway";
import {
  dependencyDown,
  isSuperseded,
  SERVER_UNREACHABLE,
  shouldDrop,
  wait,
} from "./refreshSignals";

const REFRESH_KEY = "sm_refresh";
const UNAVAILABLE_TRIES = 3;
const UNAVAILABLE_BACKOFF_MS = 100;
const REFRESH_TIMEOUT_MS = 10_000;

interface Flight {
  generation: number;
  memory: { set(token: string | null): void };
}
interface CrossTab {
  installRefreshListener(): void;
  lockRefresh<T>(fn: () => Promise<T>): Promise<T | undefined>;
  askPeerAccess(refresh: string): Promise<string | null>;
  publishRefresh(access: string, refresh: string): void;
}
interface Host {
  dropSession(state: { generation: number }): void;
  unavailable(message: string): void;
  engine(): Promise<CrossTab>;
}

class RefreshAbandoned extends Error {
  constructor() {
    super("refresh abandoned");
    this.name = "RefreshAbandoned";
  }
}

let host: Host = {
  dropSession() {},
  unavailable() {},
  engine: () => Promise.reject(new Error("refresh host missing")),
};

export function attachRefreshHost(next: Host): void {
  host = next;
}

export function readRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY) || null;
  } catch {
    return null;
  }
}
function writeRefresh(token: string): void {
  try {
    localStorage.setItem(REFRESH_KEY, token);
  } catch {
    return;
  }
}
function sessionFrom(body: unknown): RefreshedSession | null {
  const data = (body as { data?: RefreshedSession } | null)?.data;
  if (!data?.access_token) return null;
  return data;
}
async function applySession(
  state: Flight,
  session: RefreshedSession,
  gen: number,
  sent: string,
): Promise<RefreshedSession> {
  if (gen !== state.generation) throw new RefreshAbandoned();
  const crossTab = await host.engine();
  if (gen !== state.generation) throw new RefreshAbandoned();
  // A peer logout clears or replaces the token while this request is in flight.
  const stored = readRefresh();
  if (stored == null || stored !== sent) throw new RefreshAbandoned();
  state.memory.set(session.access_token);
  if (!session.refresh_token) return session;
  writeRefresh(session.refresh_token);
  crossTab.publishRefresh(session.access_token, session.refresh_token);
  return session;
}
function bareClient() {
  return axios.create({ baseURL: config.apiBaseUrl, withCredentials: false });
}
async function exchange(
  state: Flight,
  token: string,
  gen: number,
): Promise<RefreshedSession> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), REFRESH_TIMEOUT_MS);
  try {
    const res = await bareClient().post(
      "/auth/refresh",
      { refresh_token: token },
      { signal: ac.signal },
    );
    const session = sessionFrom(res.data);
    if (!session) throw new AxiosError("Refresh returned no access token");
    return await applySession(state, session, gen, token);
  } finally {
    clearTimeout(timer);
  }
}
function failRefresh(state: Flight, gen: number, err: unknown): void {
  if (gen !== state.generation || err instanceof RefreshAbandoned) return;
  if (shouldDrop(err)) host.dropSession(state);
  else if (!isSuperseded(err)) host.unavailable(SERVER_UNREACHABLE);
}
function retryDown(
  err: unknown,
  n: number,
  gen: number,
  state: Flight,
): boolean {
  return (
    dependencyDown(err) && n < UNAVAILABLE_TRIES - 1 && gen === state.generation
  );
}
async function postRefresh(
  state: Flight,
  token: string,
  gen: number,
  retry: boolean,
): Promise<RefreshedSession> {
  for (let n = 0; n < UNAVAILABLE_TRIES; n += 1) {
    try {
      return await exchange(state, token, gen);
    } catch (err) {
      if (err instanceof RefreshAbandoned) throw err;
      const stored = readRefresh();
      if (retry && isSuperseded(err) && stored)
        return postRefresh(state, stored, gen, false);
      if (!retryDown(err, n, gen, state)) {
        failRefresh(state, gen, err);
        throw err;
      }
      await wait(UNAVAILABLE_BACKOFF_MS * (n + 1));
      if (gen !== state.generation) throw new RefreshAbandoned();
    }
  }
  throw new AxiosError("Refresh unavailable");
}
async function insideLock(
  state: Flight,
  gen: number,
  before: string | null,
): Promise<RefreshedSession> {
  if (gen !== state.generation) throw new RefreshAbandoned();
  const now = readRefresh();
  if (!now) {
    if (gen === state.generation) host.dropSession(state);
    throw new RefreshAbandoned();
  }
  if (!(before && now !== before)) return postRefresh(state, now, gen, true);
  const access = await (await host.engine()).askPeerAccess(now);
  if (gen !== state.generation) throw new RefreshAbandoned();
  const stored = readRefresh();
  if (stored == null || stored !== now) throw new RefreshAbandoned();
  if (!access) return postRefresh(state, stored, gen, true);
  state.memory.set(access);
  return { access_token: access, refresh_token: stored };
}
async function adoptIfRotated(
  state: Flight,
  gen: number,
  before: string | null,
): Promise<RefreshedSession> {
  if (gen !== state.generation) throw new RefreshAbandoned();
  const now = readRefresh();
  if (!(before && now && now !== before)) throw new RefreshAbandoned();
  const access = await (await host.engine()).askPeerAccess(now);
  if (gen !== state.generation || readRefresh() !== now || !access)
    throw new RefreshAbandoned();
  state.memory.set(access);
  return { access_token: access, refresh_token: now };
}
export async function runRefresh(
  state: Flight,
  gen: number,
): Promise<RefreshedSession> {
  const crossTab = await host.engine();
  crossTab.installRefreshListener();
  // Captured before the lock: a peer may rotate storage while we wait.
  const before = readRefresh();
  const locked = await crossTab.lockRefresh(() =>
    insideLock(state, gen, before),
  );
  if (locked) return locked;
  return adoptIfRotated(state, gen, before);
}
